import { useQuery, useQueryClient } from "@tanstack/react-query";
import { orderBy } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type {
  CommunityFeedSelectableAudienceV2Dto,
  CommunityGroupDetailsV2Dto,
  MessageCreateRequest,
  MessageV2Dto,
} from "@/api/types";
import { EntityPreview } from "@/components/EntityPreview/EntityPreview";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormMentionComposer } from "@/components/Form/FormMentionComposer";
import { FormSelect } from "@/components/Form/FormSelect";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { ContentBlock } from "@/design-system/ContentBlock";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { Spread } from "@/design-system/Spread";
import type { MessageRouteStates } from "@/helpers/community-feed";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import { createRequiredStringRule } from "@/helpers/rules";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadDocument } from "@/hooks/Network/useUploadDocument";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useSlug } from "@/hooks/useSlug";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { MAX_LENGTH_RESIDENT_POST_CONTENT, MAX_LENGTH_RESIDENT_POST_TITLE } from "./helpers";
import { PromotedGroupCard } from "./PromotedGroupCard";
import { ResidentAudienceGroupOption } from "./ResidentAudienceGroupOption";
import { ResidentCreatePostAttachmentField } from "./ResidentCreatePostModalAttachmentField";
import type { CommunityFeedAudienceItem, ResidentCreatePostModalFormValues } from "./types";

const FORM_ID = "resident-create-post-modal-form";

type ResidentCreatePostModalProps = {
  type: MessageV2Dto["messageLabel"] | "promote" | null;
  groupId?: string;
  isGeneral?: boolean;
  groupToBePromoted?: CommunityGroupDetailsV2Dto;
} & ModalBaseProps;

export function ResidentCreatePostModal({
  isOpened,
  onOpenChange,
  type,
  groupId,
  isGeneral,
  groupToBePromoted,
}: ResidentCreatePostModalProps): React.ReactNode {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectId = useProjectId();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const navigate = useNavigate();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();

  const postType = type === "promote" ? "inform" : type;
  const args = groupToBePromoted
    ? ({ Type: "post", ExcludeProjectConnections: true } as const)
    : ({ Type: "post" } as const);
  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: postAudience,
    isLoading: isLoadingAudience,
    error: errorLoadingAudience,
  } = useQuery(communityFeedQueries.audience({ query: args }));
  const addMessageMutation = communityFeedMutations.useAddMessage();
  const { uploadFormImage } = useUploadImage();
  const { uploadFormVideo, abortUploadVideos } = useUploadVideo({});
  const { uploadFormDocument } = useUploadDocument();

  const groups = useMemo(() => {
    if (!postAudience) {
      return [];
    }

    function toType<T extends CommunityFeedAudienceItem["type"]>(
      type: T,
    ): (value: CommunityFeedAudienceItem["value"]) => CommunityFeedAudienceItem {
      return (value) =>
        ({
          type,
          value,
        }) as CommunityFeedAudienceItem;
    }

    const audience: CommunityFeedAudienceItem[] = [];
    if (postType === "sellItem") {
      audience.push(
        ...postAudience.interestGroups.filter((x) => x.id === sessionUser.marketPlaceGroupId).map(toType("group")),
      );
    } else if (postType === "inform") {
      audience.push(
        ...postAudience.interestGroups.filter((x) => x.id !== sessionUser.marketPlaceGroupId).map(toType("group")),
      );
      audience.push(...postAudience.realEstateGroups.map(toType("group")));
      if (postAudience.project) {
        audience.push(toType("project")(postAudience.project));
      }
      audience.push(...postAudience.projectConnections.map(toType("projectConnection")));
    } else {
      audience.push(...postAudience.helpCategories.map(toType("group")));
      audience.push(
        ...postAudience.interestGroups.filter((x) => x.id !== sessionUser.marketPlaceGroupId).map(toType("group")),
      );
      audience.push(...postAudience.realEstateGroups.map(toType("group")));
      if (postAudience.project) {
        audience.push(toType("project")(postAudience.project));
      }
      audience.push(...postAudience.projectConnections.map(toType("projectConnection")));
    }

    if (groupToBePromoted) {
      return audience.filter((x) => x.value.id !== groupToBePromoted.id);
    }

    return audience;
  }, [postType, postAudience, sessionUser, groupToBePromoted]);
  const defaultGroup: CommunityFeedAudienceItem | undefined = useMemo(() => {
    let group: CommunityFeedAudienceItem | undefined = undefined;

    if (groupId) {
      group = groups.find((x) => x.type === "group" && x.value.id === groupId);
    } else if (isGeneral) {
      group = groups.find((x) => x.type === "project");
    } else if (postType === "sellItem") {
      group = groups[0];
    }

    return group;
  }, [groupId, isGeneral, groups, postType]);
  const defaultFormValues: ResidentCreatePostModalFormValues = useMemo(() => {
    return {
      title: groupToBePromoted
        ? t("page.message-feed.resident-create-post.form.title.placeholder.promote-group", {
            groupName: groupToBePromoted.name,
          })
        : "",
      content: groupToBePromoted
        ? t("page.message-feed.resident-create-post.form.content.placeholder.promote-group")
        : "",
      contentTemplate: undefined,
      mentions: undefined,
      documents: [],
      images: [],
      videos: [],
      group: defaultGroup,
      hideFromAdmin: false,
      audience:
        defaultGroup?.type === "group"
          ? orderBy(
              defaultGroup.value.selectableAudience,
              (x) => x.type === defaultGroup.value.defaultPostingLevel,
              "desc",
            )[0]
          : undefined,
    };
  }, [groupToBePromoted, defaultGroup, t]);
  const form = useForm<ResidentCreatePostModalFormValues>({
    defaultValues: defaultFormValues,
    values: {
      ...defaultFormValues,
      group: defaultGroup,
    },
  });

  useEffect(() => {
    if (isOpened) {
      form.reset();
    }
  }, [isOpened, form]);

  const handleCancel = () => {
    // Cancel all ongoing video uploads
    abortUploadVideos(form.getValues("videos"));
  };

  async function handleSubmit(values: ResidentCreatePostModalFormValues) {
    if (!postType) {
      return;
    }

    setIsSubmitting(true);

    const imageUploadPromises = values.images.map((image) => uploadFormImage(image));
    // Curently only 1 document is allowed
    const documentUploadPromises = values.documents.map((document) => uploadFormDocument(document));
    // Curently only 1 video is allowed
    const videoUploadPromises = values.videos.map((video) => uploadFormVideo(video));

    const imageIds = (await Promise.allSettled(imageUploadPromises))
      .map((image) => (image.status === "fulfilled" ? image.value?.id : ""))
      .filter(Boolean) as string[];
    const documentIds = (await Promise.allSettled(documentUploadPromises))
      .map((document) => (document.status === "fulfilled" ? document.value?.id : ""))
      .filter(Boolean) as string[];
    const videoIds = (await Promise.allSettled(videoUploadPromises.map((video) => video)))
      .map((video) => (video.status === "fulfilled" ? video.value?.id : ""))
      .filter(Boolean) as string[];

    let projectConnectionId: string | undefined;
    if (values.group?.type === "projectConnection") {
      projectConnectionId = values.group.value.id;
    } else if (values.audience?.type === "projectConnection") {
      projectConnectionId = values.audience.projectConnection?.id;
    }

    const data: MessageCreateRequest = {
      type: "undefined",
      messageLabel: postType,
      groupId: values.group?.type === "group" ? values.group.value.id : undefined,
      projectConnectionId,
      title: values.title,
      content: values.content,
      contentTemplate: values.contentTemplate,
      mentions: values.mentions,
      isHiddenFromAdmins:
        !groupToBePromoted &&
        values.audience?.type !== "projectConnection" &&
        values.group?.type !== "projectConnection"
          ? values.hideFromAdmin
          : false,
      imageIds,
      documentIds,
      videoIds,
      relatedGroupId: groupToBePromoted ? groupToBePromoted.id : undefined,
    };

    // Only create message when all attachments were uploaded successfully
    if (
      imageIds.length === values.images.length &&
      documentIds.length === values.documents.length &&
      videoIds.length === values.videos.length
    ) {
      addMessageMutation.mutate(
        { data },
        {
          onSuccess: (createdMessage) => {
            onOpenChange(false);
            showFlashToast({ variant: "success", message: t("page.message-feed.create.notifications.success") });

            if (groupToBePromoted) {
              void queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.COMMUNITY_GROUP_DETAILS(projectId, groupToBePromoted.id),
              });
            }

            void navigate(
              routes.communityFeed.list({
                slug,
              }),
              {
                state: { newMessageId: createdMessage.id } satisfies MessageRouteStates["list"],
              },
            );
          },
        },
      );
    }

    setIsSubmitting(false);
  }

  let title = "";
  let description = "";
  if (type === "promote" ? groupId : undefined) {
    title = t("page.message-feed.resident-create-post.title.promote-group");
    description = t("page.message-feed.resident-create-post.subtitle.promote-group");
  } else {
    switch (type) {
      case "askHelp":
        title = t("page.message-feed.resident-create-post.title.help");
        description = t("page.message-feed.resident-create-post.subtitle.help");
        break;
      case "inform":
        title = t("page.message-feed.resident-create-post.title.inform");
        description = t("page.message-feed.resident-create-post.subtitle.inform");
        break;
      case "sellItem":
        title = t("page.message-feed.resident-create-post.title.marketplace");
        description = t("page.message-feed.resident-create-post.subtitle.marketplace");
        break;
    }
  }

  const currGroup = useWatch({ control: form.control, name: "group" });
  const currAudience = useWatch({ control: form.control, name: "audience" });
  const currContent = useWatch({ control: form.control, name: "content" });
  const currHideFromAdmin = useWatch({ control: form.control, name: "hideFromAdmin" });

  const { entity: attachedEntity } = unfurlDeepLinkFromContent(currContent);
  const promotedGroupImage = groupToBePromoted?.image || sessionUser.project.backgroundImage;
  const isLoading = !postType || isLoadingAudience;

  const isAttachmentFieldVisible = !groupToBePromoted;
  const mentionGroupId = currGroup?.type === "group" ? currGroup.value.id : undefined;
  const isHideFromAdminOptionVisible =
    !groupToBePromoted &&
    currAudience?.type !== "projectConnection" &&
    currGroup?.type !== "projectConnection" &&
    sessionUser.allowHidePostForAdmin;
  let mentionProjectConnectionId: string | undefined;
  if (currGroup?.type === "projectConnection") {
    mentionProjectConnectionId = currGroup.value.id;
  } else if (currAudience?.type === "projectConnection") {
    mentionProjectConnectionId = currAudience.projectConnection?.id;
  }

  let titlePlaceholder = t("page.message-feed.resident-create-post.form.title.placeholder.marketplace");
  if (postType === "askHelp") {
    titlePlaceholder = t("page.message-feed.resident-create-post.form.title.placeholder.help");
  } else if (postType === "inform") {
    titlePlaceholder = t("page.message-feed.resident-create-post.form.title.placeholder.inform");
  }

  let contentPlaceholder = t("page.message-feed.resident-create-post.form.content.placeholder.marketplace");
  if (postType === "askHelp") {
    contentPlaceholder = t("page.message-feed.resident-create-post.form.content.placeholder.help");
  } else if (postType === "inform") {
    contentPlaceholder = t("page.message-feed.resident-create-post.form.content.placeholder.inform");
  }

  return (
    <Modal.Root
      data-testid="resident-create-post-modal"
      height={680}
      isPending={isLoading}
      {...{ title, description, isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} data-testid="resident-create-post-form" formMethods={form} onSubmit={handleSubmit}>
          <>
            {!isLoadingAudience && errorLoadingAudience && (
              <Box padding={{ y: "xl" }}>
                <Notice variant="danger" message={t("page.message-feed.resident-create-post.loading.error")} />
              </Box>
            )}
            {!isLoadingAudience && !errorLoadingAudience && postType && (
              <Form.Body>
                <FormField label={t("page.message-feed.resident-create-post.form.audience.title")} required>
                  <FormSelect<ResidentCreatePostModalFormValues, CommunityFeedAudienceItem>
                    name="group"
                    placeholder={t("page.message-feed.resident-create-post.form.audience.placeholder")}
                    items={groups}
                    keySelector={(x) => x.type + x.value.id}
                    renderSelected={(x) => <ResidentAudienceGroupOption group={x} isPreview />}
                    renderOption={(x) => <ResidentAudienceGroupOption group={x} />}
                    groupSelector={(x) => {
                      if (x.type === "group") {
                        switch (x.value.type) {
                          case "helpCategory":
                            return t(
                              "page.message-feed.resident-create-post.form.audience.option-groups.help-categories",
                            );
                          case "interest":
                            return t(
                              "page.message-feed.resident-create-post.form.audience.option-groups.interest-groups",
                            );
                          default:
                            return t("page.message-feed.resident-create-post.form.audience.option-groups.real-estate");
                        }
                      } else {
                        if (x.type === "project") {
                          return t("page.message-feed.resident-create-post.form.audience.option-groups.message-all");
                        }

                        return t("page.message-feed.resident-create-post.form.audience.option-groups.connections");
                      }
                    }}
                    onChange={(x) => {
                      if (x?.type === "group") {
                        const defaultAudience = x.value.selectableAudience.find(
                          (a) => a.type === x.value.defaultPostingLevel,
                        );

                        form.setValue("audience", defaultAudience);
                        if (defaultAudience?.type === "projectConnection") {
                          form.setValue("hideFromAdmin", false);
                        }
                      }

                      if (x?.type === "projectConnection") {
                        form.setValue("hideFromAdmin", false);
                      }
                    }}
                    disabled={currGroup && (postType === "sellItem" || type !== "promote")}
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: t("page.message-feed.resident-create-post.form.audience.title"),
                      }),
                    }}
                  />
                  {currGroup?.type === "group" &&
                  (currGroup.value.selectableAudience.length > 1 ||
                    (currGroup.value.selectableAudience.length === 1 &&
                      currGroup.value.selectableAudience[0].type === "projectConnection")) ? (
                    <section className="flex flex-col gap-space-xs pt-space-sm">
                      <span className="text-caption">{t("page.admin-create-post.form.audience.audience.label")}</span>
                      <FormSelect<ResidentCreatePostModalFormValues, CommunityFeedSelectableAudienceV2Dto>
                        name="audience"
                        items={currGroup.value.selectableAudience}
                        onChange={(x) => {
                          form.setValue("audience", x);

                          if (x?.type === "projectConnection") {
                            form.setValue("hideFromAdmin", false);
                          }
                        }}
                        keySelector={(x) => x.type + (x.project?.id || x.projectConnection?.id)}
                        renderOption={(x) => (
                          <Spread>
                            <span>{x.project?.name || x.projectConnection?.name || ""}</span>
                            <span className="text-caption text-text-secondary italic">
                              {t("page.message-feed.resident-create-post.form.audience.group.members", {
                                count: x.project?.audienceCount || x.projectConnection?.audienceCount || 0,
                              })}
                            </span>
                          </Spread>
                        )}
                        groupSelector={(x) => {
                          if (x.type === "project") {
                            return t("page.message-feed.resident-create-post.form.audience.option-groups.message-all");
                          }

                          return t("page.message-feed.resident-create-post.form.audience.option-groups.connections");
                        }}
                        disabled={currGroup.value.selectableAudience.length === 1}
                      />
                    </section>
                  ) : null}
                </FormField>
                <FormField label={t("page.message-feed.resident-create-post.form.title.title")} required>
                  <FormInput<ResidentCreatePostModalFormValues>
                    name="title"
                    placeholder={titlePlaceholder}
                    rules={{
                      validate: {
                        required: createRequiredStringRule(
                          t,
                          "page.message-feed.resident-create-post.form.title.title",
                        ),
                      },
                      maxLength: {
                        message: t("components.form.error.max-length", { length: MAX_LENGTH_RESIDENT_POST_TITLE }),
                        value: MAX_LENGTH_RESIDENT_POST_TITLE,
                      },
                    }}
                  />
                </FormField>
                <FormField label={t("page.message-feed.resident-create-post.form.content.title")} required>
                  <FormMentionComposer<ResidentCreatePostModalFormValues>
                    name="content"
                    contentTemplateName="contentTemplate"
                    mentionsName="mentions"
                    placeholder={contentPlaceholder}
                    groupId={mentionGroupId}
                    projectConnectionId={mentionProjectConnectionId}
                    hideFromAdmins={isHideFromAdminOptionVisible ? currHideFromAdmin : false}
                    type="undefined"
                    size="large"
                    rules={{
                      validate: {
                        required: createRequiredStringRule(
                          t,
                          "page.message-feed.resident-create-post.form.content.title",
                        ),
                      },
                      maxLength: {
                        message: t("components.form.error.max-length", { length: MAX_LENGTH_RESIDENT_POST_CONTENT }),
                        value: MAX_LENGTH_RESIDENT_POST_CONTENT,
                      },
                    }}
                  />
                </FormField>
                {groupToBePromoted && (
                  <PromotedGroupCard
                    id={groupToBePromoted.id}
                    name={groupToBePromoted.name}
                    description={groupToBePromoted.description}
                    image={promotedGroupImage}
                    isReadonly
                    isResidentGroup={groupToBePromoted.isResidentGroup}
                  />
                )}
                {isAttachmentFieldVisible && <ResidentCreatePostAttachmentField />}
                {attachedEntity && (
                  <ContentBlock width="flow" align="left">
                    <EntityPreview
                      entityId={attachedEntity.payload.entityId}
                      entityType={attachedEntity.payload.entityType}
                      otherIds={{
                        folderId: attachedEntity.payload.folderId,
                      }}
                      readOnly
                      shouldLoad
                    />
                  </ContentBlock>
                )}
                {isHideFromAdminOptionVisible && (
                  <FormCheckbox<ResidentCreatePostModalFormValues>
                    name="hideFromAdmin"
                    label={t("page.message-feed.resident-create-post.form.hide-from-admins.title")}
                  />
                )}
              </Form.Body>
            )}
          </>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions isSticky>
        <Modal.Close>
          <Button variant="outline" tone="primary" onClick={handleCancel} disabled={isSubmitting}>
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button form={FORM_ID} variant="filled" type="submit" isLoading={isSubmitting}>
          {t("common.action.post")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
